/**
 * The pieces every screen is built from. They carry the classes; `styles.css`
 * carries the look. Nothing here uses an inline `style` attribute — the site's
 * content security policy allows no inline styles on this origin.
 */
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { Check, ChevronLeft, Close, Info } from "./icons.tsx";

function classes(...parts: (string | false | undefined)[]): string {
  return parts.filter((part): part is string => Boolean(part)).join(" ");
}

export function Card({
  children,
  class: extra,
}: {
  children: ComponentChildren;
  class?: string | undefined;
}) {
  return <div class={classes("card", extra)}>{children}</div>;
}

interface RowProps {
  leading?: ComponentChildren | undefined;
  title: ComponentChildren;
  meta?: ComponentChildren | undefined;
  body?: ComponentChildren | undefined;
  trailing?: ComponentChildren | undefined;
  class?: string | undefined;
}

/** A line inside a card that holds its own controls. */
export function Row({ leading, title, meta, body, trailing, class: extra }: RowProps) {
  return (
    <div class={classes("row", extra)}>
      {leading}
      <div class="row-text">
        <div class="row-title">{title}</div>
        {meta && <div class="row-meta">{meta}</div>}
        {body}
      </div>
      {trailing && <div class="row-end">{trailing}</div>}
    </div>
  );
}

/** A line that is itself the target, for a list that navigates. */
export function RowLink({
  leading,
  title,
  meta,
  trailing,
  onClick,
  label,
}: Omit<RowProps, "body" | "class"> & { onClick: () => void; label: string }) {
  return (
    <button type="button" class="row row-link" aria-label={label} onClick={onClick}>
      {leading}
      <div class="row-text">
        <div class="row-title">{title}</div>
        {meta && <div class="row-meta">{meta}</div>}
      </div>
      {trailing && <div class="row-end">{trailing}</div>}
    </button>
  );
}

export function DigitCircle({ digit, size }: { digit: string; size: "sm" | "md" }) {
  return (
    <span class={classes("digit-circle", size === "sm" && "digit-sm")}>{digit}</span>
  );
}

export type ButtonTone = "primary" | "secondary" | "quiet" | "bad";

interface ButtonProps
  extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, "class"> {
  tone?: ButtonTone;
  full?: boolean;
  /** In flight: the button says `busyLabel` and refuses further clicks. */
  busy?: boolean | undefined;
  busyLabel?: string | undefined;
  children: ComponentChildren;
}

export function Button({
  tone = "secondary",
  full,
  busy,
  busyLabel,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      class={classes("btn", `btn-${tone}`, full && "btn-full")}
      disabled={disabled === true || busy === true}
      {...rest}
    >
      {busy === true && busyLabel !== undefined ? busyLabel : children}
    </button>
  );
}

interface ScreenHeadProps {
  /** The words on the way back, when "Back" is not specific enough. */
  label?: string;
  onBack?: (() => void) | undefined;
  disabled?: boolean | undefined;
  /** Whatever the screen puts opposite its way back, such as an overflow menu. */
  trailing?: ComponentChildren | undefined;
}

/** The bar above every screen but home: a way back, and the screen's own control. */
export function ScreenHead({
  label = "Back",
  onBack,
  disabled,
  trailing,
}: ScreenHeadProps) {
  return (
    <div class="screen-head">
      {onBack && (
        <Button tone="quiet" disabled={disabled} onClick={onBack}>
          <ChevronLeft />
          <span>{label}</span>
        </Button>
      )}
      {trailing}
    </div>
  );
}

export type PillTone = "enabled" | "disabled" | "brand";

/** A message or a number is enabled or it is not. Others pass a label. */
const PILL_WORDS: Partial<Record<PillTone, string>> = {
  enabled: "Enabled",
  disabled: "Disabled",
};

export function Pill({ tone, label }: { tone: PillTone; label?: string | undefined }) {
  return <span class={`pill pill-${tone}`}>{label ?? PILL_WORDS[tone]}</span>;
}

export type CalloutTone = "good" | "info" | "bad";

interface CalloutProps {
  tone: CalloutTone;
  title: string;
  children?: ComponentChildren | undefined;
  onDismiss?: (() => void) | undefined;
}

/**
 * Every place the old UI printed a bare error paragraph. Colour never carries
 * the meaning: each one has an icon and a bold first line.
 */
export function Callout({ tone, title, children, onDismiss }: CalloutProps) {
  const box = useRef<HTMLDivElement | null>(null);

  // A refusal is about something the person just did, and on a long list the
  // control they used can be far below this line. `nearest` does nothing when
  // the callout is already on screen.
  useEffect(() => {
    if (tone === "bad") box.current?.scrollIntoView({ block: "nearest" });
  }, [tone, title]);

  return (
    <div
      class={`callout callout-${tone}`}
      role={tone === "bad" ? "alert" : "status"}
      ref={box}
    >
      <span class="callout-icon">{tone === "good" ? <Check /> : <Info />}</span>
      <div class="callout-text">
        <strong>{title}</strong>
        {children && <div>{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          class="icon-btn"
          aria-label={`Dismiss: ${title}`}
          onClick={onDismiss}
        >
          <Close />
        </button>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  /** Hidden labels still reach a screen reader; the placeholder does not. */
  hideLabel?: boolean | undefined;
  help?: ComponentChildren | undefined;
  counter?: { used: number; limit: number } | undefined;
  /** The control's own `id`, which is what the label points at. */
  id: string;
  children: ComponentChildren;
}

export function Field({ label, hideLabel, help, counter, id, children }: FieldProps) {
  const over = counter !== undefined && counter.used > counter.limit;
  return (
    <div class="field">
      <label class={classes("field-label", hideLabel && "visually-hidden")} for={id}>
        {label}
      </label>
      {children}
      {(help || counter) && (
        <div class="field-foot">
          {help ? <span class="field-help">{help}</span> : <span />}
          {counter && (
            <span class={classes("field-counter", over && "field-counter-over")}>
              {counter.used} / {counter.limit}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface ConfirmProps {
  title: string;
  body: string;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const FOCUSABLE =
  "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

/** Tab and Shift+Tab wrap inside the dialog, so nothing behind it takes focus. */
function trapTab(event: KeyboardEvent, dialog: HTMLElement | null): void {
  if (!dialog) return;
  const stops = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (node) => !node.hasAttribute("disabled"),
  );
  const first = stops[0];
  const last = stops.at(-1);
  if (!first || !last) {
    // Every control is disabled mid-work. Disabling the focused button blurs
    // it, so this puts focus back rather than only swallowing the key.
    event.preventDefault();
    dialog.focus();
    return;
  }
  const here = document.activeElement;
  if (event.shiftKey ? here === first || here === dialog : here === last) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}

/**
 * In-page rather than `window.confirm`, so it can name what happens. A real
 * modal because the page under it scrolls: in normal flow it opens off screen
 * above the row whose menu asked for it, which reads as nothing happening while
 * a destructive default sits one Enter key away (10.9).
 *
 * The dialog uses `.card` as a class rather than going through `<Card>`,
 * because the dialog role has to sit on the element that holds the actions.
 */
/** Two dialogs open at once would race on the body class; the last one out wins. */
let openDialogs = 0;

export function Confirm({
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: ConfirmProps) {
  const dialog = useRef<HTMLDivElement | null>(null);
  const id = useId();
  // The listeners outlive the render they were added in, so they read the
  // current handler rather than the one this closed over on mount. Cancelling
  // is refused mid-work, the same as the Cancel button is disabled.
  const dismiss = useRef({ onCancel, busy });
  dismiss.current = { onCancel, busy };

  useEffect(() => {
    const opener = document.activeElement;
    openDialogs++;
    document.body.classList.add("modal-open");
    dialog.current?.focus();

    function cancel(): void {
      if (dismiss.current.busy !== true) dismiss.current.onCancel();
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") cancel();
      else if (event.key === "Tab") trapTab(event, dialog.current);
    }

    /** Anywhere but the dialog is the backdrop; nothing else is clickable. */
    function onPointer(event: MouseEvent): void {
      if (!dialog.current?.contains(event.target as Node)) cancel();
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      if (--openDialogs === 0) document.body.classList.remove("modal-open");
      // A confirmed delete removes the row the trigger was in, so falling back
      // to the screen's own title is the difference between landing somewhere
      // and landing on `body` with nothing announced.
      const back =
        opener instanceof HTMLElement && opener.isConnected
          ? opener
          : document.querySelector<HTMLElement>("[data-screen-title]");
      back?.focus();
    };
  }, []);

  return (
    <div class="modal-backdrop">
      <div
        class="card confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-body`}
        tabIndex={-1}
        ref={dialog}
      >
        <strong class="confirm-title" id={`${id}-title`}>
          {title}
        </strong>
        <p class="confirm-body" id={`${id}-body`}>
          {body}
        </p>
        <div class="confirm-actions">
          <Button tone="quiet" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button tone="bad" busy={busy} busyLabel="Working…" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The screen's heading. Focus moves here on every screen change, which is why
 * it takes a `tabindex` it never gets from tabbing.
 */
export function ScreenTitle({
  children,
  centred,
}: {
  children: ComponentChildren;
  centred?: boolean;
}) {
  return (
    <h2
      class={classes("screen-title", centred && "centred")}
      tabIndex={-1}
      data-screen-title
    >
      {children}
    </h2>
  );
}
